import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarClock, ArrowLeft, Edit, Users, ClipboardList } from "lucide-react";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISAL_CYCLES, APPRAISALS } from "../../constants/api";

import StatusPill from "../../components/appraisal/StatusPill";
import { useAppraisalPermissions } from "../../hooks/useAppraisalRole";

const Field = ({ label, value }) => (
    <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{value || "—"}</p>
    </div>
);

const AppraisalCycleDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { fetch } = useApiMethods();
    const { canEditCycle } = useAppraisalPermissions();

    const [cycle, setCycle] = useState(null);
    const [appraisals, setAppraisals] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [cycleRes, appRes] = await Promise.all([
                fetch(APPRAISAL_CYCLES, { id }),
                fetch(APPRAISALS, { scope: "all", cycle: id }),
            ]);
            setCycle(cycleRes?.data || null);
            setAppraisals(appRes?.data || []);
        } catch (_) {
            ToastMsg("error", "Failed to load cycle");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) {
        return (
            <ContainerCard>
                <div className="text-center py-12 text-muted-foreground">Loading…</div>
            </ContainerCard>
        );
    }

    if (!cycle) {
        return (
            <ContainerCard>
                <ModuleTitleCard Icon={CalendarClock} title="Cycle Not Found" />
                <div className="px-3">
                    <Button variant="outline" size="sm" onClick={() => navigate("/appraisal/cycles")}>
                        <ArrowLeft className="w-4 h-4 mr-1" /> Back to Cycles
                    </Button>
                </div>
            </ContainerCard>
        );
    }

    const progress = {
        total: appraisals.length,
        submitted: appraisals.filter((a) => a.self_submitted_at).length,
        approved: appraisals.filter((a) => a.status === "hr_approved" || a.status === "published").length,
        published: appraisals.filter((a) => a.status === "published").length,
    };

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={CalendarClock}
                title={cycle.name}
                subtitle={cycle.description || "Cycle details and employee participation"}
            />

            <div className="px-3 pb-6 space-y-4">
                <div className="flex items-center justify-between gap-2">
                    <Button variant="ghost" size="sm" onClick={() => navigate("/appraisal/cycles")}>
                        <ArrowLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                    {canEditCycle && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/appraisal/cycles/edit/${cycle.id}`)}
                        >
                            <Edit className="w-4 h-4 mr-1" /> Edit Cycle
                        </Button>
                    )}
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        <h2 className="text-base font-semibold">Overview</h2>
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full capitalize bg-sidebar/10 text-sidebar dark:bg-sidebar-primary/20 dark:text-sidebar-primary">
                            {cycle.status}
                        </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        <Field label="Period" value={`${dayjs(cycle.period_start).format("DD MMM YYYY")} – ${dayjs(cycle.period_end).format("DD MMM YYYY")}`} />
                        <Field label="Self-Eval Window" value={`${dayjs(cycle.self_eval_start).format("DD MMM")} – ${dayjs(cycle.self_eval_end).format("DD MMM YYYY")}`} />
                        <Field label="RM Deadline" value={cycle.rm_review_deadline ? dayjs(cycle.rm_review_deadline).format("DD MMM YYYY") : "—"} />
                        <Field label="HR Deadline" value={cycle.hr_review_deadline ? dayjs(cycle.hr_review_deadline).format("DD MMM YYYY") : "—"} />
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                        { icon: Users, label: "Employees", value: progress.total, accent: "bg-sidebar/15 text-sidebar dark:bg-sidebar-primary/20 dark:text-sidebar-primary" },
                        { icon: ClipboardList, label: "Submitted", value: progress.submitted, accent: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
                        { icon: ClipboardList, label: "HR Approved", value: progress.approved, accent: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" },
                        { icon: ClipboardList, label: "Published", value: progress.published, accent: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" },
                    ].map((s, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                            <div className={`p-2 rounded-md ${s.accent}`}>
                                <s.icon className="w-4 h-4" />
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground">{s.label}</p>
                                <p className="text-lg font-semibold">{s.value}</p>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                    <h2 className="text-base font-semibold mb-3">Employees in this Cycle</h2>
                    {appraisals.length === 0 ? (
                        <div className="text-center py-6 text-sm text-muted-foreground">No employees enrolled yet.</div>
                    ) : (
                        <div className="overflow-x-auto rounded-md border border-border">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/40">
                                    <tr className="text-left text-muted-foreground">
                                        <th className="px-3 py-2 font-medium">Employee</th>
                                        <th className="px-3 py-2 font-medium hidden sm:table-cell">Reporting Manager</th>
                                        <th className="px-3 py-2 font-medium">Status</th>
                                        <th className="px-3 py-2 font-medium hidden md:table-cell">Submitted</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {appraisals.map((a) => (
                                        <tr key={a.id} className="border-t border-border hover:bg-muted/20">
                                            <td className="px-3 py-2">
                                                <p className="font-medium">{a.employee?.name || "—"}</p>
                                                <p className="text-xs text-muted-foreground">{a.employee?.designation}</p>
                                            </td>
                                            <td className="px-3 py-2 hidden sm:table-cell">{a.reporting_manager?.name || "—"}</td>
                                            <td className="px-3 py-2"><StatusPill value={a.status} /></td>
                                            <td className="px-3 py-2 hidden md:table-cell text-xs text-muted-foreground">
                                                {a.self_submitted_at ? dayjs(a.self_submitted_at).format("DD MMM YYYY") : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </ContainerCard>
    );
};

export default AppraisalCycleDetail;
